import { useEffect, useState } from "react";
import { Button } from "@codelittinc/carbon-design-system";
import { Eye, Search } from "lucide-react";
import type { ManagedUser, ManageUsersResponse } from "@carbon-os/server/contract";
import { forgetReporters } from "../lib/video";
import { resetAnalytics } from "../lib/analytics";
import { sessionFetch } from "../lib/session";

export function ManageUsers() {
  const [users, setUsers] = useState<ManagedUser[] | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [opening, setOpening] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    setUsers(null);
    void (async () => {
      try {
        const res = await sessionFetch("/api/admin/users", { cache: "no-store", signal: controller.signal });
        const body = await res.json() as ManageUsersResponse & { error?: string };
        if (!res.ok || !Array.isArray(body.users)) throw new Error(body.error ?? "The user list could not be loaded.");
        if (!controller.signal.aborted) setUsers(body.users);
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "The user list could not be loaded.");
      }
    })();
    return () => controller.abort();
  }, [attempt]);
  const viewAs = async (user: ManagedUser) => {
    setOpening(user.id);
    setError(null);
    try {
      const res = await sessionFetch(`/api/admin/view-as/${encodeURIComponent(user.id)}`, { method: "POST" });
      if (!res.ok) {
        const body = await res.json() as { error?: string };
        throw new Error(body.error ?? "The preview could not be started.");
      }
      forgetReporters();
      resetAnalytics();
      // A document navigation discards every previous user's in-memory result.
      window.location.assign("/");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The preview could not be started.");
      setOpening(null);
    }
  };
  const visible = users?.filter((user) => `${user.name} ${user.email}`.toLowerCase().includes(query.trim().toLowerCase()));
  return <section className="space-y-6">
    <div>
      <h1 className="text-2xl font-semibold text-text-primary">Manage users</h1>
      <p className="mt-2 text-sm text-text-muted">View Carbon OS as a team member to see their tools, training, and handbook. Previews are read-only.</p>
    </div>
    <div className="relative max-w-md">
      <Search aria-hidden className="absolute top-3 left-3 size-4 text-text-muted" />
      <input aria-label="Search users" placeholder="Search by name or email" value={query} onChange={(event) => setQuery(event.target.value)} className="w-full rounded-md border border-border bg-surface py-2.5 pr-3 pl-9 text-sm text-text-primary" />
    </div>
    {error && <div role="alert" className="space-y-3 text-sm text-text-secondary"><p>{error}</p><Button variant="outline" onClick={() => setAttempt((value) => value + 1)}>Try again</Button></div>}
    {!users && !error && <p role="status" className="text-sm text-text-muted">Loading users…</p>}
    {visible && <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-border-subtle text-text-muted"><tr><th scope="col" className="px-4 py-3 font-medium">User</th><th scope="col" className="px-4 py-3 font-medium">Email</th><th scope="col" className="px-4 py-3"><span className="sr-only">Actions</span></th></tr></thead>
        <tbody>{visible.map((user) => <tr key={user.id} className="border-b border-border-subtle last:border-0">
          <td className="px-4 py-3 font-medium text-text-primary">{user.name}</td>
          <td className="px-4 py-3 text-text-secondary">{user.email}</td>
          <td className="px-4 py-3 text-right"><Button variant="outline" disabled={opening !== null} aria-label={`View as ${user.name}`} onClick={() => void viewAs(user)}><Eye size={14} aria-hidden />{opening === user.id ? "Opening…" : "View as"}</Button></td>
        </tr>)}</tbody>
      </table>
      {visible.length === 0 && <p className="px-4 py-8 text-center text-sm text-text-muted">{users?.length ? "No users match your search." : "No users are available to preview."}</p>}
    </div>}
  </section>;
}

export function PreviewBanner({ user }: { user: ManagedUser }) {
  return <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-accent-muted p-4" role="status">
    <div><p className="text-sm font-medium text-text-primary">Viewing as {user.name}</p><p className="mt-1 text-xs text-text-secondary">{user.email} · Read-only preview. You are still signed in as yourself.</p></div>
    <StopPreview />
  </div>;
}

export function StopPreview() {
  const [error, setError] = useState(false);
  const [stopping, setStopping] = useState(false);
  const stop = async () => {
    setStopping(true);
    setError(false);
    try {
      const res = await sessionFetch("/api/admin/view-as", { method: "DELETE" });
      if (!res.ok) throw new Error("stop failed");
      forgetReporters();
      resetAnalytics();
      window.location.assign("/manage-users");
    } catch { setError(true); setStopping(false); }
  };
  return <div>
    <Button variant="outline" disabled={stopping} onClick={() => void stop()}>{stopping ? "Returning…" : "Stop viewing as"}</Button>
    {error && <p role="alert" className="mt-2 text-sm text-error-text">Could not return to your account. Try again.</p>}
  </div>;
}
